// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// assertNoDollarKeys (#371) — the class-wide guard for the Convex write boundary.
//
// WHY THIS EXISTS (the whole bug class in one place). Convex reserves object
// field names that start with "$": a single such key ANYWHERE in a mutation's
// args — top level, nested, or inside an array — throws at ARGUMENT-ENCODING
// time (verified against the real `convexToJson` encoder, see the test). And
// `mirrorMutationToConvex` catches that throw by design ("never let a Convex
// hiccup break the Mongo write"). For a convexOnly user there is NO Mongo write
// to fall back on, so the caught throw means the ENTIRE write is silently
// discarded while the UI reports success. This is the exact failure that bit
// #326 (attributes), #328 (custom notes) and #339 (users) — each time in ONE
// method, fixed in that method. #371 closes the CLASS: a method-agnostic
// pre-flight scan at the single mirror chokepoint so that
//   - a $-key is caught by US with a precise path, not by Convex opaquely, and
//   - a convexOnly discard is surfaced (mirror re-throws) instead of faking 200.
//
// This mirrors the real encoder EXACTLY (cross-checked in the test):
//   - flags ONLY keys that start with "$", at any object/array depth
//   - Mongo dot-paths ("a.b"), leading-underscore keys ("_id"), and a literal
//     "$" inside a STRING VALUE are all fine — Convex accepts them.
//
// The Mongo operator (e.g. `$set`) is a wire detail that belongs at the Lambda
// boundary, not in Convex args — unwrap it before the mirror (see userSetFields
// for the users path / lambdaWire's toLambdaBody for the reverse-sync re-apply).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Deep-scan a value for the FIRST object key that starts with "$".
 *
 * @param {*} value any JSON-ish value (objects + arrays are descended)
 * @param {string} [path] internal accumulator for the dotted/bracketed key path
 * @returns {string|null} the path to the offending key (e.g. "updateFields.$push"
 *   or "list[2].$bad"), or null when the value is free of $-prefixed keys.
 */
export function findDollarKeyPath(value, path = "") {
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const hit = findDollarKeyPath(value[i], `${path}[${i}]`);
      if (hit) return hit;
    }
    return null;
  }
  // Plain objects only. Dates, null, primitives have no descendable keys.
  if (value && typeof value === "object") {
    for (const key of Object.keys(value)) {
      const here = path ? `${path}.${key}` : key;
      if (key.startsWith("$")) return here;
      const hit = findDollarKeyPath(value[key], here);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * Throw a precise, identifiable error if `value` carries any $-prefixed key.
 * Called at the mirror chokepoint before a Convex mutation is encoded, so the
 * failure names the offending key path instead of surfacing Convex's opaque
 * "Field name $x starts with a '$'" — and so a future caller that reintroduces
 * a Mongo operator into Convex args fails loudly (and in tests) rather than
 * silently voiding a convexOnly write.
 *
 * @param {*} value the args object about to cross the Convex boundary
 * @param {string} [context] a label (usually the method name) for the message
 * @throws {Error} when a $-prefixed key is present at any depth
 */
export function assertNoDollarKeys(value, context = "") {
  const hit = findDollarKeyPath(value);
  if (hit) {
    throw new Error(
      `convex-boundary: $-prefixed key "${hit}"` +
        (context ? ` in ${context}` : "") +
        ` cannot cross the Convex boundary — it throws at argument-encoding time ` +
        `and would be silently discarded (#371/#339).`
    );
  }
}
